import React, { useRef, useEffect, useState, forwardRef } from "react";
import { chakra } from "@chakra-ui/react";

const PATTERNS = [
  { name: "Glider", cells: [[1,0],[2,1],[0,2],[1,2],[2,2]] },
  { name: "Lightweight Spaceship", cells: [
    [1,0],[2,0],[3,0],[4,0],[0,1],[4,1],[4,2],[0,3],[3,3]
  ]},
  { name: "Pulsar", cells: [
    [2,0],[3,0],[4,0],[8,0],[9,0],[10,0],
    [0,2],[5,2],[7,2],[12,2],
    [0,3],[5,3],[7,3],[12,3],
    [0,4],[5,4],[7,4],[12,4],
    [2,5],[3,5],[4,5],[8,5],[9,5],[10,5],
    [2,7],[3,7],[4,7],[8,7],[9,7],[10,7],
    [0,8],[5,8],[7,8],[12,8],
    [0,9],[5,9],[7,9],[12,9],
    [0,10],[5,10],[7,10],[12,10],
    [2,12],[3,12],[4,12],[8,12],[9,12],[10,12],
  ]},
  { name: "Pentadecathlon", cells: [[0,2],[1,0],[1,2],[1,4],[2,0],[2,2],[2,4],[3,2]] },
  { name: "R-pentomino", cells: [[1,0],[2,0],[0,1],[1,1],[1,2]] },
  { name: "Acorn", cells: [[1,0],[3,1],[0,2],[1,2],[4,2],[5,2],[6,2]] },
  { name: "Diehard", cells: [[6,0],[0,1],[1,1],[1,2],[5,2],[6,2],[7,2]] },
  { name: "Gosper Glider Gun", cells: [
    [1,5],[1,6],[2,5],[2,6],[11,5],[11,6],[11,7],[12,4],[12,8],
    [13,3],[13,9],[14,3],[14,9],[15,6],[16,4],[16,8],[17,5],
    [17,6],[17,7],[18,6],[21,3],[21,4],[21,5],[22,3],[22,4],
    [22,5],[23,2],[23,6],[25,1],[25,2],[25,6],[25,7],[35,3],
    [35,4],[36,3],[36,4],
  ]},
];

function randomInitCells(nX, nY, patternsToDrop) {
  let cells = Array.from({ length: nX }, () => Array(nY).fill(0));
  const shuffled = PATTERNS.sort(() => 0.5 - Math.random());
  const chosen = shuffled.slice(0, Math.min(patternsToDrop, PATTERNS.length));
  const patternFits = (cells, pattern, ox, oy) => {
    return pattern.every(
      ([x, y]) =>
        cells[ox + x] !== undefined &&
        cells[ox + x][oy + y] !== undefined &&
        cells[ox + x][oy + y] === 0
    );
  };
  chosen.forEach((pat) => {
    const xs = pat.cells.map(([x]) => x),
      ys = pat.cells.map(([_, y]) => y);
    const w = Math.max(...xs) + 1,
      h = Math.max(...ys) + 1;
    let tries = 30,
      ox,
      oy;
    do {
      ox = Math.floor(Math.random() * (nX - w));
      oy = Math.floor(Math.random() * (nY - h));
    } while (tries-- > 0 && !patternFits(cells, pat.cells, ox, oy));
    if (patternFits(cells, pat.cells, ox, oy)) {
      pat.cells.forEach(([x, y]) => {
        cells[ox + x][oy + y] = 1;
      });
    }
  });
  return cells;
}

const LifeGame = forwardRef(function LifeGame(
  {
    width = 320,
    height = 240,
    cellSize = 16,
    liveCellColor = "rgba(238,125,133,1)",
    deadCellColor = "#fff",
    gridColor = "#e1e1e1",
    speed = 70,
    patternsToDrop = 5,
    style = {},
    ...restProps
  },
  ref
) {
  const canvasRef = useRef(null);
  const [hoverCell, setHoverCell] = useState(null); // [x, y] or null
  const [paused, setPaused] = useState(false);
  const [drawing, setDrawing] = useState(false); // is mouse down?

  const nX = Math.floor(width / cellSize);
  const nY = Math.floor(height / cellSize);
  const cW = width / nX;
  const cH = height / nY;

  // Only randomize on first mount (or page reload)
  const [cells, setCells] = useState(() => randomInitCells(nX, nY, patternsToDrop));

  // Always keep a mutable copy for animation & click editing
  const cellGridRef = useRef({ cells, nX, nY, cW, cH });

  useEffect(() => {
    cellGridRef.current = { cells, nX, nY, cW, cH };
  }, [cells, nX, nY, cW, cH]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    canvas.width = width;
    canvas.height = height;

    function draw(currentCells) {
      ctx.clearRect(0, 0, width, height);
      currentCells.forEach((row, x) =>
        row.forEach((cell, y) => {
          const isHovered = hoverCell && hoverCell[0] === x && hoverCell[1] === y;
          ctx.beginPath();
          ctx.rect(x * cW, y * cH, cW, cH);
          ctx.fillStyle = (cell || isHovered) ? liveCellColor : deadCellColor;
          ctx.fill();
          ctx.strokeStyle = gridColor;
          ctx.stroke();
        })
      );
    }

    function neighbours(cells, x, y) {
      let n = 0;
      for (let dx = -1; dx <= 1; dx++)
        for (let dy = -1; dy <= 1; dy++)
          if ((dx || dy) && cells[x + dx]?.[y + dy]) n++;
      return n;
    }

    function update() {
      if (paused) {
        draw(cellGridRef.current.cells); // still redraw on hover
        return;
      }
      let oldCells = cellGridRef.current.cells;
      let newCells = oldCells.map((row, x) =>
        row.map((cell, y) => {
          const n = neighbours(oldCells, x, y);
          return cell ? +(n === 2 || n === 3) : +(n === 3);
        })
      );
      cellGridRef.current.cells = newCells;
      setCells(newCells); // Update for React state, to trigger hover redraws
      draw(newCells);
    }

    draw(cellGridRef.current.cells);
    const timer = setInterval(update, speed);

    return () => clearInterval(timer);
  }, [
    width, height, cW, cH,
    liveCellColor, deadCellColor, gridColor, speed,
    hoverCell, paused,
  ]);

  // Helper: get cell coordinates from mouse event
  const getCellFromEvent = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const x = Math.floor(((e.clientX - rect.left) / rect.width) * nX);
    const y = Math.floor(((e.clientY - rect.top) / rect.height) * nY);
    if (
      x >= 0 && x < nX &&
      y >= 0 && y < nY
    ) {
      return [x, y];
    }
    return null;
  };

  const handleMouseMove = (e) => {
    const cell = getCellFromEvent(e);
    setHoverCell(cell);
    if (drawing && cell) {
      const [x, y] = cell;
      const next = cellGridRef.current.cells.map(row => row.slice());
      if (next[x][y] !== 1) {
        next[x][y] = 1;
        cellGridRef.current.cells = next;
        setCells(next);
      }
    }
  };

  const handleMouseEnter = () => setPaused(true);
  const handleMouseLeave = () => { setHoverCell(null); setPaused(false); setDrawing(false); };

  const handleMouseDown = (e) => {
    setDrawing(true);
    const cell = getCellFromEvent(e);
    if (cell) {
      const [x, y] = cell;
      const next = cellGridRef.current.cells.map(row => row.slice());
      if (next[x][y] !== 1) {
        next[x][y] = 1;
        cellGridRef.current.cells = next;
        setCells(next);
      }
    }
  };

  const handleMouseUp = () => setDrawing(false);

  // Forward Chakra + ref
  const setRefs = (el) => {
    canvasRef.current = el;
    if (typeof ref === "function") ref(el);
    else if (ref) ref.current = el;
  };

  return (
    <chakra.canvas
      ref={setRefs}
      width={width}
      height={height}
      tabIndex={0}
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      {...restProps}
      style={{
        display: "block",
        background: deadCellColor,
        cursor: "pointer",
        outline: "none",
        userSelect: "none",
        ...style,
      }}
    />
  );
});

export default LifeGame;
